// src/pages/LoginPage.js
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ username + password
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [err, setErr] = useState("");
  const [showPw, setShowPw] = useState(false);

  // ✅ Forgot password nice UI (no alert)
  const [showForgot, setShowForgot] = useState(false);

  // ✅ dark mode toggle (default: LIGHT / white background)
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem("lp_dark");
    return saved === "1";
  });

  // ✅ error shake trigger
  const [shake, setShake] = useState(false);

  useEffect(() => {
    localStorage.setItem("lp_dark", dark ? "1" : "0");
  }, [dark]);

  const triggerShake = () => {
    setShake(true);
    window.setTimeout(() => setShake(false), 520);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    try {
      const fromRaw = location.state?.from;

      // ✅ handle BOTH:
      // 1) fromRaw is STRING: "/user/calendar"
      // 2) fromRaw is OBJECT: { pathname: "/user/calendar", ... }
      const fromPath = typeof fromRaw === "string" ? fromRaw : fromRaw?.pathname;

      console.log("[LOGIN] location.state.from =", fromRaw);
      console.log("[LOGIN] fromPath =", fromPath);

      const user = await login(username, password);
      console.log("[LOGIN] logged in user =", user);

      // ✅ Force password change
      if (user?.must_change_password) {
        console.log("[LOGIN] must_change_password=1 -> redirect -> /change-password");
        navigate("/change-password", { replace: true });
        return;
      }

      // ✅ if user tried to open a protected page first, go back there
      if (fromPath && fromPath !== "/login") {
        console.log("[LOGIN] redirecting back to:", fromPath);
        navigate(fromPath, { replace: true });
        return;
      }

      // ✅ default redirect by role
      // ✅ default redirect by role
      const role = String(user?.role || "").toLowerCase();

      // allow admin area for superadmin (and you can add manager too if you want)
      const isAdmin =
        role === "superadmin" ||
        role === "manager" ||
        role === "ops_supervisor" ||
        role === "lead";


      if (isAdmin) {
        console.log("[LOGIN] redirect default -> /admin/dashboard");
        navigate("/admin/dashboard", { replace: true });
      } else {
        console.log("[LOGIN] redirect default -> /my-schedule");
        navigate("/my-schedule", { replace: true });
      }
    } catch (e2) {
      setErr(e2.message || "Login failed");
      triggerShake();
    }
  };

  return (
    <div className={`lp-shell ${dark ? "lp-dark" : ""}`}>
      <style>{`
        @import url("https://fonts.googleapis.com/css2?family=Inter:wght@500;600;700;800;900&display=swap");

        :root{
          --lp-blue:#2f6bff;
          --lp-blue-2:#4f7dff;

          --lp-ink:#1f2a44;
          --lp-muted:#6b7aa6;
          --lp-line: rgba(31,42,68,.14);

          /* ✅ DEFAULT = WHITE BACKGROUND */
          --lp-bg:#ffffff;
          --lp-field:#ffffff;

          --lp-focus: rgba(47,107,255,.14);
          --lp-glow: rgba(47,107,255,.10);

          --lp-danger:#dc2626;
          --lp-dangerGlow: rgba(220,38,38,.18);
        }

        /* ✅ Dark mode (only when toggle on) */
        .lp-dark{
          --lp-bg:#0b1020;
          --lp-field:#0f1733;
          --lp-ink:#e8eeff;
          --lp-muted:#a8b6e6;
          --lp-line: rgba(232,238,255,.14);
          --lp-focus: rgba(47,107,255,.22);
          --lp-glow: rgba(47,107,255,.18);
          --lp-dangerGlow: rgba(220,38,38,.22);
        }

        .lp-shell{
          min-height: 100dvh;
          background: var(--lp-bg);
          display:flex;
          flex-direction:column;
          font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
          position: relative;
          overflow: hidden;
        }

        /* ✅ top glow (tight + clean) */
        .lp-shell:before{
          content:"";
          position:absolute;
          left: 50%;
          top: -170px;
          transform: translateX(-50%);
          width: 860px;
          height: 360px;
          background: radial-gradient(circle, rgba(47,107,255,.12), transparent 62%);
          pointer-events:none;
        }

        /* ✅ top right toggle */
        .lp-topbar{
          position: absolute;
          top: 16px;
          right: 16px;
          z-index: 5;
        }

        .lp-toggle{
          height: 40px;
          padding: 0 12px;
          border-radius: 999px;
          border: 1px solid var(--lp-line);
          background: rgba(255,255,255,.82);
          color: var(--lp-ink);
          font-weight: 800;
          display:flex;
          align-items:center;
          gap: 8px;
          cursor: pointer;
          box-shadow: 0 10px 18px var(--lp-glow);
          transition: transform .12s ease, background .18s ease, box-shadow .18s ease;
          backdrop-filter: blur(10px);
          user-select:none;
        }
        .lp-dark .lp-toggle{
          background: rgba(15,23,51,.75);
          color: var(--lp-ink);
        }
        .lp-toggle:hover{
          transform: translateY(-1px);
          box-shadow: 0 14px 22px var(--lp-glow);
        }
        .lp-toggle:active{
          transform: translateY(0px) scale(.99);
        }
        .lp-toggle .dot{
          width: 10px;
          height: 10px;
          border-radius: 999px;
          background: linear-gradient(180deg, var(--lp-blue-2), var(--lp-blue));
          box-shadow: 0 0 0 3px rgba(47,107,255,.12);
        }

        /* ===== Center ===== */
        .lp-center{
          flex: 1;
          display:flex;
          align-items:center;
          justify-content:center;
          padding: 18px 14px 34px;
          position: relative;
          z-index: 1;
        }

        /* ✅ Panel */
        .lp-panel{
          width: min(560px, 94vw);
          border-radius: 18px;
          padding: 22px 18px 18px;
          background: rgba(255,255,255,.78);
          border: 1px solid rgba(31,42,68,.10);
          box-shadow: 0 26px 70px rgba(31,42,68,.12);
          backdrop-filter: blur(10px);
        }
        .lp-dark .lp-panel{
          background: rgba(15,23,51,.58);
          border: 1px solid rgba(232,238,255,.10);
          box-shadow: 0 26px 80px rgba(0,0,0,.38);
        }

        .lp-card{
          width: 100%;
          display:flex;
          flex-direction:column;
          align-items:center;
          text-align:center;
        }

        /* logo row */
        .lp-brandrow{
          display:flex;
          align-items:center;
          gap: 12px;
          margin-bottom: 8px;
        }

        .lp-logo-wrap{
          width: 54px;
          height: 54px;
          border-radius: 14px;
          overflow:hidden;
          background: rgba(255,255,255,.95);
          box-shadow: 0 12px 22px var(--lp-glow);
          border: 1px solid var(--lp-line);
          display:flex;
          align-items:center;
          justify-content:center;
        }
        .lp-dark .lp-logo-wrap{
          background: rgba(255,255,255,.97);
        }

        .lp-logo{
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          transform: scale(4.25);
          transform-origin: 50% 24%;
        }

        /* ✅ Brand */
        .lp-brand{
          font-size: 46px;
          line-height: 1;
          font-weight: 900;
          letter-spacing: .2px;
          font-family: "Brush Script MT", "Segoe Script", "Lucida Handwriting", cursive;
          display:flex;
          align-items:baseline;
          user-select:none;
          gap: 0;

          text-shadow:
            0 2px 10px rgba(47,107,255,.24),
            0 0 2px rgba(0,0,0,.20);
        }
        .lp-dark .lp-brand{
          text-shadow:
            0 2px 12px rgba(47,107,255,.30),
            0 0 1px rgba(255,255,255,.08);
        }

        /* ✅ Smooth pop-in on page load (NO BLUR) */
        .lp-brandpop{
          display:inline-flex;
          animation: lp-pop 520ms cubic-bezier(.2,.9,.2,1) both;
        }
        @keyframes lp-pop{
          0%{
            opacity: 0;
            transform: translateY(6px) scale(.985);
          }
          100%{
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        /* ✅ Two-color brand text (NO BOX) */
        .lp-brandtext-frank{
          color: var(--lp-blue);
        }
        .lp-brandtext-istan{
          color: var(--lp-ink);
        }
        .lp-dark .lp-brandtext-istan{
          color: #e8eeff;
        }

        /* underline under "istan" */
        .lp-istan{
          position: relative;
          display:inline-block;
          padding-bottom: 6px;
        }
        .lp-istan::after{
          content:"";
          position:absolute;
          left: 2px;
          right: 6px;
          bottom: 1px;
          height: 3px;
          border-radius: 99px;
          background: linear-gradient(90deg, rgba(47,107,255,.22), rgba(47,107,255,.92));
          box-shadow: 0 10px 18px rgba(47,107,255,.16);
          transform: scaleX(.94);
          transform-origin: left;
        }

        .lp-welcome{
          margin-top: 8px;
          font-size: 30px;
          font-weight: 900;
          color: var(--lp-ink);
          letter-spacing: -0.4px;
        }

        .lp-sub{
          margin-top: 6px;
          font-size: 15.5px;
          color: var(--lp-muted);
          font-weight: 650;
        }

        /* ✅ Error shake */
        .lp-shake{
          animation: lp-shake .5s ease;
        }
        @keyframes lp-shake{
          0%{ transform: translateX(0); }
          15%{ transform: translateX(-7px); }
          30%{ transform: translateX(7px); }
          45%{ transform: translateX(-6px); }
          60%{ transform: translateX(6px); }
          75%{ transform: translateX(-4px); }
          90%{ transform: translateX(4px); }
          100%{ transform: translateX(0); }
        }

        /* Form */
        .lp-form{
          width: min(400px, 92vw);
          margin-top: 20px;
          display:flex;
          flex-direction:column;
          gap: 12px;
        }

        .lp-field{
          height: 46px;
          border-radius: 12px;
          border: 1px solid rgba(31,42,68,.16);
          background: var(--lp-field);
          padding: 0 12px;
          font-size: 15px;
          color: var(--lp-ink);
          outline: none;
          box-shadow: 0 10px 22px rgba(31,42,68,.06);
          transition: box-shadow .18s ease, border-color .18s ease, transform .12s ease, background .18s ease;
        }
        .lp-dark .lp-field{
          border: 1px solid rgba(232,238,255,.16);
          box-shadow: 0 12px 26px rgba(0,0,0,.26);
        }

        .lp-field::placeholder{
          color: rgba(31,42,68,.55);
          font-weight: 650;
        }
        .lp-dark .lp-field::placeholder{
          color: rgba(232,238,255,.55);
        }

        .lp-field:focus{
          border-color: rgba(47,107,255,.65);
          box-shadow: 0 0 0 4px var(--lp-focus), 0 12px 26px rgba(47,107,255,.12);
        }

        /* ✅ red glow on fields when error */
        .lp-errorOn .lp-field{
          border-color: rgba(220,38,38,.60);
          box-shadow: 0 0 0 4px var(--lp-dangerGlow), 0 12px 26px rgba(220,38,38,.10);
        }

        .lp-pwwrap{
          position: relative;
          width: 100%;
        }

        .lp-pwwrap input{
          width: 100%;
          padding-right: 52px;
        }

        .lp-eye{
          position:absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          height: 34px;
          width: 40px;
          border: 1px solid var(--lp-line);
          background: rgba(31,42,68,.03);
          cursor: pointer;
          border-radius: 12px;
          color: rgba(31,42,68,.55);
          font-size: 16px;
          display:flex;
          align-items:center;
          justify-content:center;
          transition: background .15s ease, color .15s ease, transform .12s ease;
        }
        .lp-dark .lp-eye{
          background: rgba(232,238,255,.05);
          color: rgba(232,238,255,.60);
        }

        .lp-eye:hover{
          background: rgba(47,107,255,.10);
          color: rgba(31,42,68,.82);
        }
        .lp-dark .lp-eye:hover{
          color: rgba(232,238,255,.92);
        }

        .lp-eye:active{
          transform: translateY(-50%) scale(.98);
        }

        /* ✅ improved button animation */
        .lp-btn{
          height: 46px;
          border-radius: 12px;
          border: 1px solid rgba(47,107,255,.70);
          background: linear-gradient(180deg, rgba(47,107,255,.22), rgba(47,107,255,.12));
          color: var(--lp-blue);
          font-weight: 850;
          font-size: 16px;
          cursor: pointer;
          display:flex;
          align-items:center;
          justify-content:center;
          gap: 8px;
          box-shadow: 0 16px 34px rgba(47,107,255,.18);
          transition: transform .12s ease, box-shadow .18s ease, background .18s ease;
          position: relative;
          overflow: hidden;
        }

        /* shine sweep */
        .lp-btn::after{
          content:"";
          position:absolute;
          top:-60%;
          left:-60%;
          width: 45%;
          height: 220%;
          transform: rotate(20deg);
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,.48) 50%, transparent 100%);
          opacity: 0;
        }

        .lp-btn:hover{
          background: linear-gradient(180deg, rgba(47,107,255,.28), rgba(47,107,255,.14));
          box-shadow: 0 20px 44px rgba(47,107,255,.22);
          transform: translateY(-1px);
        }
        .lp-btn:hover::after{
          opacity: 1;
          animation: lp-btnshine .9s ease forwards;
        }
        @keyframes lp-btnshine{
          0%{ left:-60%; }
          100%{ left:140%; }
        }

        .lp-btn:active{
          transform: translateY(0px) scale(.99);
        }

        .lp-forgot{
          margin-top: 6px;
          text-align:right;
          color: var(--lp-blue);
          font-weight: 750;
          font-size: 13px;
          cursor: pointer;
          user-select:none;
        }
        .lp-forgot:hover{
          text-decoration: underline;
        }

        .lp-error{
          width: min(400px, 92vw);
          margin-top: 10px;
          color: var(--lp-danger);
          font-weight: 800;
          font-size: 13px;
          background: rgba(239,68,68,.08);
          border: 1px solid rgba(239,68,68,.18);
          padding: 10px 12px;
          border-radius: 12px;
          box-shadow: 0 12px 26px rgba(220,38,38,.10);
        }

        /* ✅ Forgot password inline card (nice UI, no alert) */
        .lp-forgotCard{
          width: min(400px, 92vw);
          margin-top: 10px;
          border-radius: 14px;
          border: 1px solid rgba(47,107,255,.22);
          background: rgba(47,107,255,.06);
          box-shadow: 0 14px 30px rgba(47,107,255,.12);
          padding: 12px 12px;
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap: 12px;
        }

        .lp-forgotCard .title{
          font-weight: 900;
          color: var(--lp-ink);
          font-size: 13.5px;
          margin-bottom: 4px;
        }

        .lp-forgotCard .msg{
          font-weight: 700;
          color: var(--lp-muted);
          font-size: 13px;
          line-height: 1.35;
        }

        .lp-forgotClose{
          border: 1px solid var(--lp-line);
          background: rgba(255,255,255,.85);
          color: var(--lp-ink);
          border-radius: 10px;
          height: 32px;
          padding: 0 10px;
          cursor:pointer;
          font-weight: 900;
          transition: transform .12s ease, box-shadow .18s ease, background .18s ease;
        }
        .lp-dark .lp-forgotClose{
          background: rgba(15,23,51,.75);
          color: var(--lp-ink);
        }
        .lp-forgotClose:hover{
          transform: translateY(-1px);
          box-shadow: 0 10px 18px var(--lp-glow);
        }
        .lp-forgotClose:active{
          transform: translateY(0px) scale(.99);
        }

        /* ✅ Mobile-perfect */
        @media (max-width: 520px){
          .lp-topbar{ top: 12px; right: 12px; }
          .lp-panel{ padding: 18px 14px 14px; border-radius: 16px; }
          .lp-logo-wrap{ width: 50px; height: 50px; border-radius: 14px; }
          .lp-brand{ font-size: 40px; }
          .lp-welcome{ font-size: 26px; }
        }

        @media (max-width: 380px){
          .lp-brand{ font-size: 36px; }
          .lp-welcome{ font-size: 24px; }
          .lp-sub{ font-size: 14.5px; }
          .lp-field, .lp-btn{ height: 44px; }
          .lp-toggle{ height: 38px; }
        }
      `}</style>

      {/* ✅ Dark mode toggle (white default) */}
      <div className="lp-topbar">
        <button
          type="button"
          className="lp-toggle"
          onClick={() => setDark((d) => !d)}
          title={dark ? "Switch to Light Mode" : "Switch to Dark Mode"}
          aria-label={dark ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          <span className="dot" />
          {dark ? "Dark 🌙" : "Light ☀️"}
        </button>
      </div>

      <main className="lp-center">
        <div className="lp-panel">
          <div className={`lp-card ${shake ? "lp-shake" : ""}`}>
            <div className="lp-brandrow">
              <div className="lp-logo-wrap" title="Logo">
                <img src="/logo.png" alt="Logo" className="lp-logo" />
              </div>

              {/* ✅ NO BOX + Two-color Frankistan */}
              <div className="lp-brand lp-brandpop" aria-label="Frankistan">
                <span className="lp-brandtext-frank">Frank</span>
                <span className="lp-brandtext-istan">
                  <span className="lp-istan">istan</span>
                </span>
              </div>
            </div>

            <div className="lp-welcome">Welcome Back!</div>
            <div className="lp-sub">Please enter your details to login.</div>

            {err ? <div className="lp-error">{err}</div> : null}

            <form className={`lp-form ${err ? "lp-errorOn" : ""}`} onSubmit={handleSubmit}>
              <input
                className="lp-field"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Email"
                autoComplete="username"
              />

              <div className="lp-pwwrap">
                <input
                  className="lp-field"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="lp-eye"
                  onClick={() => setShowPw((s) => !s)}
                  title={showPw ? "Hide password" : "Show password"}
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  👁
                </button>
              </div>

              <button className="lp-btn" type="submit">
                ⮕ Login
              </button>

              <div className="lp-forgot" onClick={() => setShowForgot((s) => !s)}>
                Forgot Password?
              </div>

              {showForgot ? (
                <div className="lp-forgotCard" role="alert" aria-live="polite">
                  <div>
                    <div className="title">Forgot your password?</div>
                    <div className="msg">
                      Please contact <b>Admin</b> to reset your password.
                      <br />
                      After reset, you’ll be forced to change it on next login.
                    </div>
                  </div>

                  <button
                    type="button"
                    className="lp-forgotClose"
                    onClick={() => setShowForgot(false)}
                    aria-label="Close"
                    title="Close"
                  >
                    OK
                  </button>
                </div>
              ) : null}
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}

export default LoginPage;
